import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { BrowserRouter, Routes, Route, useLocation, NavLink, Outlet } from 'react-router-dom';
import { getApiUrl, API_ENDPOINTS } from './config/apiConfig';

import { GlobalStyle } from './styles/GlobalStyle';
import Sidebar from './components/Sidebar';
import Footer from './components/Footer';
import Home from './pages/Home';
import DetailPage from './pages/DetailPage';
import WatchPage from './pages/WatchPage';
import DiscoverPage from './pages/DiscoverPage';
import ProfilePage from './pages/ProfilePage';
import SearchPage from './pages/SearchPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import AdminPage from './pages/AdminPage';
import AdminAddPage from './pages/AdminAddPage';
import AdminEditPage from './pages/AdminEditPage';
import AdminUsersPage from './pages/AdminUsersPage';
import AdminSettingsPage from './pages/AdminSettingsPage';
import AdminSEOPage from './pages/AdminSEOPage';
import AdminTMDBPage from './pages/AdminTMDBPage';
import AdminFooterPage from './pages/AdminFooterPage';
import AdminPagesPage from './pages/AdminPagesPage';
import AdminMediaLibraryPage from './pages/AdminMediaLibraryPage';
import AdminCommentsPage from './pages/AdminCommentsPage';
import DynamicPage from './pages/DynamicPage';
import LicenseActivation from './pages/LicenseActivation';
// Yeni sayfaları import ediyoruz
import AllEpisodesPage from './pages/AllEpisodesPage';
import CalendarPage from './pages/CalendarPage';

// License Check Component
import LicenseCheck from './components/LicenseCheck';
import AdminRoute from './components/AdminRoute';
import AdminOnlyRoute from './components/AdminOnlyRoute';
import SettingsPage from './pages/SettingsPage';

const AppLayout = styled.div`
  display: flex;
  min-height: 100vh;
`;

const MainContent = styled.main`
  flex-grow: 1;
  transition: margin-left 0.4s ease, width 0.4s ease;
  width: 100%;
  background-color: transparent;

  margin-left: ${props => props.$isSidebarVisible ? (props.$isSidebarExpanded ? '250px' : '90px') : '0'};
  width: ${props => props.$isSidebarVisible ? `calc(100% - ${props.$isSidebarExpanded ? '250px' : '90px'})` : '100%'};

  @media (max-width: 768px) {
    margin-left: 0 !important;
    width: 100% !important;
    padding-bottom: 80px;
  }
`;

const AdminNav = styled.div`
    display: flex;
    gap: 15px;
    padding: 50px 5% 30px 5%;
    background-color: #0b0614;
`;

const NavButton = styled(NavLink)`
    padding: 12px 25px;
    font-size: 1rem;
    font-weight: 600;
    border: none;
    border-radius: 10px;
    background: var(--glass-bg);
    color: var(--primary-color);
    cursor: pointer;
    text-decoration: none;
    border: 1px solid var(--border-color);
    transition: all 0.3s ease;

    &:hover {
        background: rgba(255, 255, 255, 0.1);
        transform: translateY(-2px);
    }

    &.active {
        background: var(--gradient-pink);
        color: white;
        border-color: var(--gradient-pink);
    }
`;

const AdminLayout = () => {
    const [userRole, setUserRole] = useState(null);

    useEffect(() => {
        const checkRole = async () => {
            try {
                const response = await fetch(getApiUrl(API_ENDPOINTS.CHECK_AUTH), {
                    credentials: 'include'
                });
                if (response.ok) {
                    const data = await response.json();
                    if (data.success && data.user) {
                        setUserRole(data.user.role);
                    }
                }
            } catch (error) {
                console.error('Role check error:', error);
            }
        };
        checkRole();
    }, []);

    const isAdmin = userRole === 'admin';
    const isModerator = userRole === 'moderator';

    return (
        <div>
            <AdminNav>
                <NavButton to="/admin" end>İçerikler</NavButton>
                {isAdmin && <NavButton to="/admin/users">Üyeler</NavButton>}
                {isAdmin && <NavButton to="/admin/comments">Yorumlar</NavButton>}
                {isAdmin && <NavButton to="/admin/settings">Ayarlar</NavButton>}
                {isAdmin && <NavButton to="/admin/seo">SEO</NavButton>}
                {isAdmin && <NavButton to="/admin/tmdb">TMDB</NavButton>}
                {isAdmin && <NavButton to="/admin/footer">Footer</NavButton>}
                {isAdmin && <NavButton to="/admin/pages">Sayfalar</NavButton>}
                {(isAdmin || isModerator) && <NavButton to="/admin/media">Medya</NavButton>}
            </AdminNav>
            <Outlet />
        </div>
    );
};

const PageWrapper = () => {
    const location = useLocation();
    const [isSidebarExpanded, setIsSidebarExpanded] = useState(false);
    
    const sidebarHiddenRoutes = ['/login', '/register', '/activate', '/admin', '/forgot-password', '/reset-password'];
    const isSidebarVisible = !sidebarHiddenRoutes.some(path => location.pathname.startsWith(path));

    return (
        <AppLayout>
            {isSidebarVisible && <Sidebar isExpanded={isSidebarExpanded} setExpanded={setIsSidebarExpanded} />}
            <MainContent $isSidebarVisible={isSidebarVisible} $isSidebarExpanded={isSidebarExpanded}>
                <Routes>
                    {/* Public routes */}
                    <Route path="/" element={<Home />} />
                    <Route path="/activate" element={<LicenseActivation />} />
                    
                    {/* Yeni route'lar */}
                    <Route path="/tum-bolumler" element={<AllEpisodesPage />} />
                    <Route path="/takvim" element={<CalendarPage />} />
                    <Route path="/kesfet" element={<DiscoverPage />} />
                    <Route path="/profil/:username" element={<ProfilePage />} />
                    <Route path="/ayarlar" element={<SettingsPage />} />
                    <Route path="/search/:query" element={<SearchPage />} />
                    <Route path="/:mediaType/:id" element={<DetailPage />} />                 
                    <Route path="/watch/movie/:id" element={<WatchPage />} />
                    <Route path="/watch/:mediaType/:id/season/:seasonNumber/episode/:episodeNumber" element={<WatchPage />} />
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/register" element={<RegisterPage />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    
                    {/* Admin Routes */}
                    <Route 
                        path="/admin" 
                        element={
                            <LicenseCheck>
                                <AdminRoute>
                                    <AdminLayout />
                                </AdminRoute>
                            </LicenseCheck>
                        }
                    >
                        <Route index element={<AdminPage />} />
                        <Route path="add" element={<AdminAddPage />} />
                        <Route path="edit/:id" element={<AdminEditPage />} />
                        <Route path="users" element={
                            <AdminOnlyRoute>
                                <AdminUsersPage />
                            </AdminOnlyRoute>
                        } />
                        <Route path="settings" element={
                            <AdminOnlyRoute>
                                <AdminSettingsPage />
                            </AdminOnlyRoute>
                        } />
                        <Route path="seo" element={
                            <AdminOnlyRoute>
                                <AdminSEOPage />
                            </AdminOnlyRoute>
                        } />
                        <Route path="tmdb" element={
                            <AdminOnlyRoute>
                                <AdminTMDBPage />
                            </AdminOnlyRoute>
                        } />
                        <Route path="footer" element={
                            <AdminOnlyRoute>
                                <AdminFooterPage />
                            </AdminOnlyRoute>
                        } />
                        <Route path="pages" element={
                            <AdminOnlyRoute>
                                <AdminPagesPage />
                            </AdminOnlyRoute>
                        } />
                        <Route path="media" element={<AdminMediaLibraryPage />} />
                        <Route path="comments" element={
                            <AdminOnlyRoute>
                                <AdminCommentsPage />
                            </AdminOnlyRoute>
                        } />
                    </Route>

                    
                    {/* Dynamic Pages - En sona koy, diğer route'larla çakışmasın */}
                    <Route path="/:page" element={<DynamicPage />} />
                </Routes>
                {isSidebarVisible && <Footer />}
            </MainContent>
        </AppLayout>
    );
}

function App() {
  const [themeColorsLoaded, setThemeColorsLoaded] = useState(false);

  useEffect(() => {
    const loadThemeColors = async () => {
      try {
        const response = await fetch(getApiUrl(API_ENDPOINTS.GET_PUBLIC_SETTINGS));
        
        // Response'un JSON olup olmadığını kontrol et
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          const text = await response.text();
          console.warn('API JSON döndürmedi, HTML döndü. URL:', getApiUrl(API_ENDPOINTS.GET_PUBLIC_SETTINGS));
          console.warn('Response:', text.substring(0, 200));
          // Hata olsa bile sayfanın yüklenmesine izin ver
          setThemeColorsLoaded(true);
          return;
        }
        
        if (response.ok) {
          const settings = await response.json();
          
          const root = document.documentElement;
          
          if (settings.theme_primary_color) {
            root.style.setProperty('--gradient-purple', settings.theme_primary_color);
          }
          if (settings.theme_secondary_color) {
            root.style.setProperty('--gradient-pink', settings.theme_secondary_color);
          }
          if (settings.theme_gradient_purple) {
            root.style.setProperty('--gradient-purple', settings.theme_gradient_purple);
          }
          if (settings.theme_gradient_pink) {
            root.style.setProperty('--gradient-pink', settings.theme_gradient_pink);
          }
          if (settings.theme_gradient_cyan) {
            root.style.setProperty('--gradient-cyan', settings.theme_gradient_cyan);
          }
          if (settings.theme_background_start) {
            root.style.setProperty('--background-start', settings.theme_background_start);
          }
          if (settings.theme_background_mid) {
            root.style.setProperty('--background-mid', settings.theme_background_mid);
          }
          if (settings.theme_background_end) {
            root.style.setProperty('--background-end', settings.theme_background_end);
          }
          if (settings.theme_button_bg_start) {
            root.style.setProperty('--button-bg-start', settings.theme_button_bg_start);
          }
          if (settings.theme_button_bg_end) {
            root.style.setProperty('--button-bg-end', settings.theme_button_bg_end);
          }
          if (settings.theme_sidebar_active_color) {
            root.style.setProperty('--sidebar-indicator-color', settings.theme_sidebar_active_color);
          }

          // Light Mode Logic
          if (settings.theme_mode === 'light') {
             // Light Theme Overrides (unless specifically set by other keys)
             if (!settings.theme_background_start) root.style.setProperty('--background-start', '#f0f2f5');
             if (!settings.theme_background_mid) root.style.setProperty('--background-mid', '#ffffff');
             if (!settings.theme_background_end) root.style.setProperty('--background-end', '#e4e6eb');
             
             root.style.setProperty('--primary-color', '#1a1a1a');
             root.style.setProperty('--secondary-color', '#4a4a4a');
             root.style.setProperty('--glass-bg', 'rgba(255, 255, 255, 0.8)');
             root.style.setProperty('--border-color', 'rgba(0, 0, 0, 0.1)');
          }
        }
      } catch (error) {
        console.error('Tema renkleri yüklenirken hata:', error);
        // Hata olsa bile sayfanın yüklenmesine izin ver
      } finally {
        setThemeColorsLoaded(true);
      }
    };

    loadThemeColors();
  }, []);

  return (
    <BrowserRouter>
      <GlobalStyle />
      {themeColorsLoaded && <PageWrapper />}
    </BrowserRouter>
  );
}

export default App;
